import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/useAuth';
import Spinner from './Spinner';
import NotFound from '../pages/NotFound';

/**
 * Gates a route on authentication and, optionally, on role.
 *
 * Before `requireRole` existed this checked authentication only, and none of
 * the admin pages checked a role of their own -- so any signed-in user who
 * typed /admin/fleet got the page. The API calls 403, so it was never a data
 * leak, but it rendered a broken page instead of refusing, and any navigation
 * that hides links by role was relying on a guard that did not exist.
 *
 * A refused route renders the not-found page rather than a "forbidden" one, so
 * the existence of admin tooling is not confirmed to users who have no reason
 * to know about it.
 */
const ProtectedRoute = ({ children, requireRole }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return <Spinner center showLabel size="lg" label={t('common.checking_auth')} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireRole && user?.role !== requireRole) {
    return <NotFound reason="forbidden" />;
  }

  return children;
};

export default ProtectedRoute;
